import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Recursion.module.css';
import FibonacciEngine from '../recursionengine/FibonacciEngine';
import { RecursionCommands } from '../../../types/RecursionCommands';

export const FIBONACCI_CODE = `
function fibonacci(n):
    if n < 2:
        return n
    else:
        return fibonacci(n - 1) + fibonacci(n - 2)
`;

interface FibonacciWrapperProps {
  command?: RecursionCommands | null;
  onComplete?: () => void;
}

const FibonacciWrapper: React.FC<FibonacciWrapperProps> = ({ 
  command: externalCommand, 
  onComplete 
}) => {
  const navigate = useNavigate();
  const [internalCommand, setInternalCommand] = useState<RecursionCommands | null>(null);
  const [inputValue, setInputValue] = useState('5');
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [finalResult, setFinalResult] = useState<number | null>(null);

  useEffect(() => {
    if (externalCommand) {
      setInternalCommand(externalCommand);
      
      if (externalCommand.action === 'generate') {
        const randomValue = Math.floor(Math.random() * 4) + 5;
        setInputValue(String(randomValue));
      }
      
      if (externalCommand.action === 'reset') {
        setFinalResult(null);
      }
    }
  }, [externalCommand]);

  const handleRun = () => {
    if (isAnimating) return;
    setInternalCommand({ id: Date.now(), action: 'run' });
  };

  const handleStop = () => {
    if (!isAnimating) return;
    setInternalCommand({ id: Date.now(), action: 'stop' });
  };

  const handleReset = () => {
    setInternalCommand({ id: Date.now(), action: 'reset' });
    setFinalResult(null);
  };

  const handleGenerate = () => {
    if (isAnimating) return;
    const randomValue = Math.floor(Math.random() * 4) + 5;
    setInputValue(String(randomValue));
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    setActiveLine(null);
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        &larr; Return
      </button>

      <h1 className={styles.title}>Recursion: Fibonacci Sequence</h1>

      <div className={styles.description}>
        <h2>Description</h2>
        <p>
          The Fibonacci sequence is a series where each number is the sum of the two preceding ones.
          The recursive definition is: <code>fib(n) = fib(n-1) + fib(n-2)</code>.
        </p>
      </div>

      <div className={styles.useCase}>
        <h2>Use Cases</h2>
        <p>Great for teaching recursion and showing exponential blowup; a classic interview problem.</p>
      </div>

      <div className={styles.timeComplexity}>
        <h2>Time Complexity</h2>
        <p>
          Time: <b>O(2^n)</b>. Space: <b>O(n)</b>.
        </p>
      </div>

      <div className={styles.controls}>
        <input
          type="number"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          disabled={isAnimating}
          min={0}
          max={10}
        />
        <button onClick={handleRun} disabled={isAnimating}>Run</button>
        <button onClick={handleStop} disabled={isAnimating}>Stop</button>
        <button onClick={handleGenerate} disabled={isAnimating}>Generate</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.vizAndCode}>
          <FibonacciEngine
            command={internalCommand}
            onComplete={handleAnimationComplete}
            inputValue={inputValue}
            setActiveLine={setActiveLine}
            isAnimating={isAnimating}
            setIsAnimating={setIsAnimating}
            setFinalResult={setFinalResult}
            finalResult={finalResult} 
          />
          <div className={styles.codeDisplay}>
            {FIBONACCI_CODE.trim().split('\n').map((line, index) => (
              <div
                key={index}
                className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}
              >
                {line || ' '}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FibonacciWrapper;